import { useMemo } from "react";
import { BottomSheetModal, BottomSheetScrollView } from "@gorhom/bottom-sheet";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import type { CameraApiResponse } from "@src/services/api/cameras-api";
import { getCameraKeyString } from "@src/utils/key-parser";
import { PressableScale } from "pressto";
import { Icon } from "@src/components/Icon";
import { AppBottomSheet } from "@src/features/common/components/AppBottomSheet";

interface CameraFilterBottomSheetProps {
  bottomSheetRef: React.RefObject<BottomSheetModal | null>;
  cameras: CameraApiResponse[];
  selectedCameraKey: string | null;
  onSelectCamera: (cameraKey: string | null) => void;
  showAllOption?: boolean;
  title?: string;
  subtitle?: string;
}

export function CameraFilterBottomSheet({
  bottomSheetRef,
  cameras,
  selectedCameraKey,
  onSelectCamera,
  showAllOption = false,
  title = "Select Camera",
  subtitle,
}: CameraFilterBottomSheetProps) {
  const { theme } = useUnistyles();

  const hasCameras = useMemo(() => cameras && cameras.length > 0, [cameras]);

  if (!hasCameras) return null;

  const effectiveSubtitle =
    subtitle ??
    (showAllOption ? "Choose a camera to filter the activity feed" : "Choose a camera to view its alarm settings");

  return (
    <AppBottomSheet
      bottomSheetRef={bottomSheetRef}
      snapPoints={["60%", "90%"]}
      enableDynamicSizing={false}
      backgroundStyle={{ backgroundColor: theme.colors.background }}
      handleIndicatorStyle={{ backgroundColor: theme.colors.border }}
    >
      <BottomSheetScrollView
        contentContainerStyle={{ paddingHorizontal: theme.spacing["5xl"], paddingVertical: theme.spacing.xl }}
      >
        <Text textStyle="lg/semibold" fontWeight="600" mb="xs" textAlign="center">
          {title}
        </Text>
        {effectiveSubtitle && (
          <Text color="textMuted" textStyle="sm/regular" mb="4xl" textAlign="center">
            {effectiveSubtitle}
          </Text>
        )}

        <View gap="md">
          {/* Optional "All cameras" option (used by Activity tab) */}
          {showAllOption && (
            <PressableScale
              onPress={() => onSelectCamera(null)}
              style={{
                backgroundColor: theme.colors.surface,
                borderWidth: 1,
                borderColor: selectedCameraKey === null ? theme.colors.navy[500] : theme.colors.border,
                padding: theme.spacing["4xl"],
                borderRadius: 8,
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <View style={{ flex: 1 }}>
                <Text textStyle="md/regular" fontWeight={selectedCameraKey === null ? "600" : "400"}>
                  All cameras
                </Text>
              </View>
              {selectedCameraKey === null && <Icon name="checkmarkOutline" size={24} color={theme.colors.navy[500]} />}
            </PressableScale>
          )}

          {/* Individual cameras (same styling as AlarmSettings sheet) */}
          {cameras.map((camera) => {
            const cameraKey = getCameraKeyString(camera);
            const displayName =
              (camera as unknown as { nickname?: string; name?: string }).nickname ||
              (camera as unknown as { nickname?: string; name?: string }).name ||
              "";
            const model = (camera as unknown as { model?: string }).model || "";
            const description = displayName && model ? `${displayName} • ${model}` : displayName || model || cameraKey;
            const isSelected = selectedCameraKey === cameraKey;

            return (
              <PressableScale
                key={cameraKey}
                onPress={() => onSelectCamera(cameraKey)}
                style={{
                  backgroundColor: theme.colors.surface,
                  borderWidth: 1,
                  borderColor: isSelected ? theme.colors.navy[500] : theme.colors.border,
                  padding: theme.spacing["4xl"],
                  borderRadius: 8,
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <View style={{ flex: 1 }}>
                  <Text textStyle="md/regular" fontWeight={isSelected ? "600" : "400"}>
                    {cameraKey}
                  </Text>
                  {description !== cameraKey && (
                    <Text textStyle="sm/regular" color="textMuted" mt="xs">
                      {description}
                    </Text>
                  )}
                </View>
                {isSelected && <Icon name="checkmarkOutline" size={24} color={theme.colors.navy[500]} />}
              </PressableScale>
            );
          })}
        </View>
      </BottomSheetScrollView>
    </AppBottomSheet>
  );
}
